import {BrgButton, ButtonVariant, useIsMobile} from "@brg/ui";
import {ModalContainer} from './styles';
import {useTranslation} from "react-i18next";
import {
    CompleteDescription,
    CompleteTitle, 
    CompleteWrapper,
    NoProjectsButton,
    NoProjectsWrapper, 
    SendProjectModalWrapper
} from "./employStyles";
import {FC, useCallback, useEffect, useMemo, useState} from "react";
import {ProjectsButchController} from "../../../core/controllers/ProjectsButchController";
import { Project, TeamAPI, GetProjectsByFilterProps} from "@brg/api";
import {useProfile} from "../../../core/hooks/auth/useProfile";
import {SortingTypeIndex} from "../../constants/sortingType";
import {useHistory} from "react-router-dom";
import {useModal} from "../../../core";
import {SendProjectsEntity} from "./SendProjectsEntity";
import { RingPositions } from "shared/constants/ringPositionType";
import SendProjectCheckbox from "./SendProjectsCheckbox";

const NoProjects: FC<{title: string, onClick: () => void, disabledCheckbox: boolean}> = ({ title, onClick, disabledCheckbox }) => {
    const {t} = useTranslation();

    return (<CompleteWrapper noProjects>
        <NoProjectsWrapper>
            <CompleteTitle noProjects style={{ color: 'rgba(144, 144, 144, 1)'}}>
                {t('teams.modal.no_projects_title', {title})}
            </CompleteTitle>
            <SendProjectCheckbox 
                checked={disabledCheckbox}
                text={t('teams.modal.without_projects')}
                onClick={onClick}
            />
        </NoProjectsWrapper>
    </CompleteWrapper>)
}

export const CompleteSend: FC<{title: string, contextName: string, withoutProjects?: boolean, isCompetition?: boolean, id?: string }> = ({title, id, isCompetition, contextName, withoutProjects}) => {

    const modal = useModal();
    const {t} = useTranslation();
    const history = useHistory();

    return (<CompleteWrapper>
        <CompleteTitle fixedWidth>
            {isCompetition ? t('teams.modal.complete_title_competition', { title }) : t('teams.modal.complete_title', {title})}
        </CompleteTitle>
        <CompleteDescription>
            {withoutProjects ? t('teams.modal.complete_description_wp') : t('teams.modal.complete_description', {title})}
        </CompleteDescription>
        <BrgButton 
            variant={ButtonVariant.Filled}
            onClick={() => {
                modal.hide();
                if (isCompetition) {
                    return;
                }
                history.push((!!contextName ? "/teams/" + contextName : "/"))
            }}
        >
            {t(`teams.buttons.back_on_page`, { name: title })} + 123
        </BrgButton>
    </CompleteWrapper>)
}



export const SendProjectForEmploy: FC<{team: any, onCompleteSend?: () => void}> = ({team, onCompleteSend = () => {}}) => {
    const isMobile = useIsMobile();
    const { t } = useTranslation();
    const [isProjectsLoading, setIsProjectsLoading] = useState(true);
    const [time, setT] = useState(new Date().getTime());
    const [projects, setProjects] = useState<Project[]>([]);
    const [filterOpt, setFilterOpt] = useState<GetProjectsByFilterProps>();
    const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
    const profile = useProfile();
    const [sorting, setSorting] = useState(1);
    const [isSendingCompleted, setIsSendingCompleted] = useState(false);
    const [requestWithoutProjects, setIsRequestWithoutProjects] = useState(false);
    const [isEmptyProjects, setIsEmptyProjects] = useState(false);
    const [checkbox, setCheckbox] = useState(false);
    
    useEffect(
        () => {
            if (team && profile && profile.accountId) {
                var filter: any = {
                    authorIds: [profile.accountId],
                    projectFilter: sorting,
                    isContextIgnored: true,
                    projectStatus: [1],
                    onlyAuthors: false,
                    contextsNotContains: true,
                };

                if (team.managedTagIds?.length > 0) {
                    filter.tagIds = team.managedTagIds;
                }

                if (team.pendingProjectIds?.length > 0) {
                    filter.excludeProjectIds = team.pendingProjectIds;
                }
                
                setFilterOpt(filter);
            }
        },
        [profile, sorting, team]
    );
    
    const onSortingChange = useCallback((s: SortingTypeIndex): void => {
        setSorting(s);
        setT(Date.now());
    }, []);

    const onSendWithoutProjects = useCallback(() => {
        setCheckbox(!checkbox);
    }, [team, checkbox])

    const alreadyProjects = useMemo(() => {
        return projects ? projects
            .filter((p) => {
                //@ts-ignore
                return p?.contexts.includes(team.contextName)
            })
            .map((p) => p?.projectId) : [];
    }, [projects, team.contextName])

    const isEnoughProjects = useMemo(() =>  {
        return !isEmptyProjects && (projects?.length === 0 || projects?.length !== alreadyProjects?.length)
    },[projects?.length, alreadyProjects?.length, isEmptyProjects]);

    const noProjectVisible = useMemo(() => (!isEnoughProjects && !isProjectsLoading && !requestWithoutProjects), [requestWithoutProjects, isProjectsLoading, isEnoughProjects ])

    const noProjectsRequestCheckbox = useMemo(() => (
        <SendProjectCheckbox text={t('teams.modal.without_projects')} checked={checkbox} onClick={onSendWithoutProjects} />
    ), [onSendWithoutProjects, checkbox, t ])

    const onMobileSortingClick = useCallback(() => {
        setSorting(sorting === 1 ? 2 : 1);
        setT(Date.now());
    }, [sorting, setSorting]);

    const onSend = useCallback(() => {
        if (!!checkbox) {
            TeamAPI.RequestMemberToTeam({ teamId: team?.teamId })
            .then(() => {
                setIsRequestWithoutProjects(true);
                onCompleteSend && onCompleteSend();
            })
            return;
        } 

        if (selectedProjects?.length === 0) return;

        TeamAPI.RequestProjectToTeam({ projectIds: selectedProjects, teamId: team.teamId || '' })
            .then((v) => {
                setIsSendingCompleted(true);
                onCompleteSend && onCompleteSend();
            })
            .catch(x => {
                throw new Error();
            })
    }, [selectedProjects, team.teamId, onCompleteSend, checkbox])

    if (!profile) {
        return null;
    }


    return <ModalContainer style={{ overflow: 'auto', height: '100%'}} justify="flex-start" offsetTop={88} offsetTopMobile={1}>
        <SendProjectModalWrapper width={'100%'} directionMobile='column' direction='column' justify={'flex-start'} align={"center"} alignMobile="flex-start" style={{ position: 'relative'}}>
            <SendProjectsEntity 
                noProjectCheckbox={noProjectsRequestCheckbox} 
                noProjectVisible={!noProjectVisible} 
                hideCount={noProjectVisible || (isSendingCompleted || requestWithoutProjects ) } 
                hideFilter={noProjectVisible || (isSendingCompleted || requestWithoutProjects ) } 
                checkbox={checkbox} 
                count={selectedProjects?.length || 0} 
                isDisabledSendButton={(selectedProjects?.length === 0 && !checkbox) || (isSendingCompleted || requestWithoutProjects ) } 
                onMobileSortingClick={onMobileSortingClick} 
                onSortingChange={onSortingChange} 
                sorting={sorting} 
                onSend={onSend} 
                isClosed={!(!isSendingCompleted)}
                hide={requestWithoutProjects || isSendingCompleted}
                fullTitle
            >
                {filterOpt && !isSendingCompleted && isEnoughProjects && !requestWithoutProjects &&
                    <ProjectsButchController
                        modal={false}
                        onSelect={projectId => {
                            setSelectedProjects((prev: string[]) => {
                                if (prev.includes(projectId)) return prev.filter(a => a !== projectId);
                                return [...prev, projectId];
                            });
                        }}
                        metaAlwaysVisible
                        isParticipate={alreadyProjects}
                        selectedProjects={selectedProjects}
                        selectable={true}
                        // isSelectRightNeed={isMobile}
                        selectRingPosition={RingPositions.right}
                        filter={filterOpt}
                        disableFilterCheck={true}
                        date={time}
                        onProjectsLoaded={(p) => {p?.length === 0 ? setIsEmptyProjects(true) : setProjects(p)}}
                        isLoading={setIsProjectsLoading}
                        withoutGoToProject={true}
                        withoutQueryParams={isMobile}
                    />
                }
            </SendProjectsEntity>
            {noProjectVisible && <NoProjects title={team?.name || ''} onClick={onSendWithoutProjects} disabledCheckbox={checkbox} />}
            {(isSendingCompleted || requestWithoutProjects ) && <CompleteSend title={team?.name || ''} contextName={team?.contextName || ''} withoutProjects={requestWithoutProjects} />}
        </SendProjectModalWrapper>
    </ModalContainer>
}import {BrgButton, ButtonVariant, useIsMobile} from "@brg/ui";
import {ModalContainer} from './styles';
import {useTranslation} from "react-i18next";
import {
    CompleteDescription,
    CompleteTitle, 
    CompleteWrapper,
    NoProjectsButton,
    NoProjectsWrapper, 
    SendProjectModalWrapper
} from "./employStyles";
import {FC, useCallback, useEffect, useMemo, useState} from "react";
import {ProjectsButchController} from "../../../core/controllers/ProjectsButchController";
import { Project, TeamAPI, GetProjectsByFilterProps} from "@brg/api";
import {useProfile} from "../../../core/hooks/auth/useProfile";
import {SortingTypeIndex} from "../../constants/sortingType";
import {useHistory} from "react-router-dom";
import {useModal} from "../../../core";
import {SendProjectsEntity} from "./SendProjectsEntity";
import { RingPositions } from "shared/constants/ringPositionType";
import SendProjectCheckbox from "./SendProjectsCheckbox";

const NoProjects: FC<{title: string, onClick: () => void, disabledCheckbox: boolean}> = ({ title, onClick, disabledCheckbox }) => {
    const {t} = useTranslation();

    return (<CompleteWrapper noProjects>
        <NoProjectsWrapper>
            <CompleteTitle noProjects style={{ color: 'rgba(144, 144, 144, 1)'}}>
                {t('teams.modal.no_projects_title', {title})}
            </CompleteTitle>
            <SendProjectCheckbox 
                checked={disabledCheckbox}
                text={t('teams.modal.without_projects')}
                onClick={onClick}
            />
        </NoProjectsWrapper>
    </CompleteWrapper>)
}

export const CompleteSend: FC<{title: string, contextName: string, withoutProjects?: boolean, isCompetition?: boolean, id?: string }> = ({title, id, isCompetition, contextName, withoutProjects}) => {

    const modal = useModal();
    const {t} = useTranslation();
    const history = useHistory();

    return (<CompleteWrapper>
        <CompleteTitle fixedWidth>
            {isCompetition ? t('teams.modal.complete_title_competition', { title }) : t('teams.modal.complete_title', {title})}
        </CompleteTitle>
        <CompleteDescription>
            {withoutProjects ? t('teams.modal.complete_description_wp') : t('teams.modal.complete_description', {title})}
        </CompleteDescription>
        <BrgButton 
            variant={ButtonVariant.Filled}
            onClick={() => {
                modal.hide();
                if (isCompetition) {
                    return;
                }
                history.push((!!contextName ? "/teams/" + contextName : "/"))
            }}
        >
            {t(`teams.buttons.back_on_page`, { name: title })} + 123
        </BrgButton>
    </CompleteWrapper>)
}



export const SendProjectForEmploy: FC<{team: any, onCompleteSend?: () => void}> = ({team, onCompleteSend = () => {}}) => {
    const isMobile = useIsMobile();
    const { t } = useTranslation();
    const [isProjectsLoading, setIsProjectsLoading] = useState(true);
    const [time, setT] = useState(new Date().getTime());
    const [projects, setProjects] = useState<Project[]>([]);
    const [filterOpt, setFilterOpt] = useState<GetProjectsByFilterProps>();
    const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
    const profile = useProfile();
    const [sorting, setSorting] = useState(1);
    const [isSendingCompleted, setIsSendingCompleted] = useState(false);
    const [requestWithoutProjects, setIsRequestWithoutProjects] = useState(false);
    const [isEmptyProjects, setIsEmptyProjects] = useState(false);
    const [checkbox, setCheckbox] = useState(false);
    
    useEffect(
        () => {
            if (team && profile && profile.accountId) {
                var filter: any = {
                    authorIds: [profile.accountId],
                    projectFilter: sorting,
                    isContextIgnored: true,
                    projectStatus: [1],
                    onlyAuthors: false,
                    contextsNotContains: true,
                };

                if (team.managedTagIds?.length > 0) {
                    filter.tagIds = team.managedTagIds;
                }

                if (team.pendingProjectIds?.length > 0) {
                    filter.excludeProjectIds = team.pendingProjectIds;
                }
                
                setFilterOpt(filter);
            }
        },
        [profile, sorting, team]
    );
    
    const onSortingChange = useCallback((s: SortingTypeIndex): void => {
        setSorting(s);
        setT(Date.now());
    }, []);

    const onSendWithoutProjects = useCallback(() => {
        setCheckbox(!checkbox);
    }, [team, checkbox])

    const alreadyProjects = useMemo(() => {
        return projects ? projects
            .filter((p) => {
                //@ts-ignore
                return p?.contexts.includes(team.contextName)
            })
            .map((p) => p?.projectId) : [];
    }, [projects, team.contextName])

    const isEnoughProjects = useMemo(() =>  {
        return !isEmptyProjects && (projects?.length === 0 || projects?.length !== alreadyProjects?.length)
    },[projects?.length, alreadyProjects?.length, isEmptyProjects]);

    const noProjectVisible = useMemo(() => (!isEnoughProjects && !isProjectsLoading && !requestWithoutProjects), [requestWithoutProjects, isProjectsLoading, isEnoughProjects ])

    const noProjectsRequestCheckbox = useMemo(() => (
        <SendProjectCheckbox text={t('teams.modal.without_projects')} checked={checkbox} onClick={onSendWithoutProjects} />
    ), [onSendWithoutProjects, checkbox, t ])

    const onMobileSortingClick = useCallback(() => {
        setSorting(sorting === 1 ? 2 : 1);
        setT(Date.now());
    }, [sorting, setSorting]);

    const onSend = useCallback(() => {
        if (!!checkbox) {
            TeamAPI.RequestMemberToTeam({ teamId: team?.teamId })
            .then(() => {
                setIsRequestWithoutProjects(true);
                onCompleteSend && onCompleteSend();
            })
            return;
        } 

        if (selectedProjects?.length === 0) return;

        TeamAPI.RequestProjectToTeam({ projectIds: selectedProjects, teamId: team.teamId || '' })
            .then((v) => {
                setIsSendingCompleted(true);
                onCompleteSend && onCompleteSend();
            })
            .catch(x => {
                throw new Error();
            })
    }, [selectedProjects, team.teamId, onCompleteSend, checkbox])

    if (!profile) {
        return null;
    }


    return <ModalContainer style={{ overflow: 'auto', height: '100%'}} justify="flex-start" offsetTop={88} offsetTopMobile={1}>
        <SendProjectModalWrapper width={'100%'} directionMobile='column' direction='column' justify={'flex-start'} align={"center"} alignMobile="flex-start" style={{ position: 'relative'}}>
            <SendProjectsEntity 
                noProjectCheckbox={noProjectsRequestCheckbox} 
                noProjectVisible={!noProjectVisible} 
                hideCount={noProjectVisible || (isSendingCompleted || requestWithoutProjects ) } 
                hideFilter={noProjectVisible || (isSendingCompleted || requestWithoutProjects ) } 
                checkbox={checkbox} 
                count={selectedProjects?.length || 0} 
                isDisabledSendButton={(selectedProjects?.length === 0 && !checkbox) || (isSendingCompleted || requestWithoutProjects ) } 
                onMobileSortingClick={onMobileSortingClick} 
                onSortingChange={onSortingChange} 
                sorting={sorting} 
                onSend={onSend} 
                isClosed={!(!isSendingCompleted)}
                hide={requestWithoutProjects || isSendingCompleted}
                fullTitle
            >
                {filterOpt && !isSendingCompleted && isEnoughProjects && !requestWithoutProjects &&
                    <ProjectsButchController
                        modal={false}
                        onSelect={projectId => {
                            setSelectedProjects((prev: string[]) => {
                                if (prev.includes(projectId)) return prev.filter(a => a !== projectId);
                                return [...prev, projectId];
                            });
                        }}
                        metaAlwaysVisible
                        isParticipate={alreadyProjects}
                        selectedProjects={selectedProjects}
                        selectable={true}
                        // isSelectRightNeed={isMobile}
                        selectRingPosition={RingPositions.right}
                        filter={filterOpt}
                        disableFilterCheck={true}
                        date={time}
                        onProjectsLoaded={(p) => {p?.length === 0 ? setIsEmptyProjects(true) : setProjects(p)}}
                        isLoading={setIsProjectsLoading}
                        withoutGoToProject={true}
                        withoutQueryParams={isMobile}
                    />
                }
            </SendProjectsEntity>
            {noProjectVisible && <NoProjects title={team?.name || ''} onClick={onSendWithoutProjects} disabledCheckbox={checkbox} />}
            {(isSendingCompleted || requestWithoutProjects ) && <CompleteSend title={team?.name || ''} contextName={team?.contextName || ''} withoutProjects={requestWithoutProjects} />}
        </SendProjectModalWrapper>
    </ModalContainer>
}